import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrency, formatDate } from "./api";
import { drawPdfBrand } from "./brand";

const NAVY = [29, 78, 216];
const SLATE_900 = [15, 23, 42];
const SLATE_500 = [100, 116, 139];
const SLATE_200 = [226, 232, 240];
const ROSE = [244, 63, 94];
const EMERALD = [16, 185, 129];
const AMBER = [245, 158, 11];
const WHITE = [255, 255, 255];

export function exportRollupPDF(data) {
  const { scope, entity, programs, projects, totals } = data;
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 36;

  // Header bar
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 56, "F");
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Compass PM", marginX, 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("by Uplift Communications Corporation", marginX, 44);
  doc.setFontSize(9);
  doc.text(`${scope === "portfolio" ? "Portfolio" : "Program"} rollup · ${new Date().toLocaleString()}`, pageW - marginX, 32, { align: "right" });
  drawPdfBrand(doc, pageW, marginX, `${scope === "portfolio" ? "Portfolio" : "Program"} rollup · ${new Date().toLocaleString()}`);

  let y = 82;

  // Title
  doc.setTextColor(...SLATE_900);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(entity.name || (scope === "portfolio" ? "Portfolio" : "Program"), marginX, y);
  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE_500);
  const meta = [];
  if (entity.owner_name || entity.owner) meta.push(`Owner: ${entity.owner_name || entity.owner}`);
  meta.push(`${scope === "portfolio" ? "Programs" : "Projects"}: ${scope === "portfolio" ? totals.program_count : totals.project_count}`);
  if (scope === "portfolio") meta.push(`Projects: ${totals.project_count}`);
  meta.push(`High-risk projects: ${totals.high_risk_projects}`);
  doc.text(meta.join("   •   "), marginX, y);
  if (entity.description) {
    y += 14;
    const descLines = doc.splitTextToSize(entity.description, pageW - marginX * 2);
    doc.text(descLines, marginX, y);
    y += descLines.length * 12;
  }
  y += 10;

  // KPI cards: Budget / Est / Actual / Variance
  const estVar = totals.estimated_cost_sum - totals.entity_budget;
  const actualVar = totals.actual_cost_sum - totals.entity_budget;
  const cards = [
    { l: `${scope === "portfolio" ? "Portfolio" : "Program"} Budget`, v: formatCurrency(totals.entity_budget), tone: SLATE_900 },
    { l: "Project Budgets", v: formatCurrency(totals.stated_budget_sum), tone: SLATE_900, sub: `${totals.project_count} projects` },
    { l: "Estimated Cost", v: formatCurrency(totals.estimated_cost_sum), tone: estVar > 0 ? ROSE : EMERALD, sub: estVar > 0 ? `${formatCurrency(estVar)} over` : `${formatCurrency(Math.abs(estVar))} under` },
    { l: "Actual Cost", v: formatCurrency(totals.actual_cost_sum), tone: actualVar > 0 ? ROSE : EMERALD, sub: actualVar > 0 ? `${formatCurrency(actualVar)} over` : `${formatCurrency(Math.abs(actualVar))} under` },
  ];
  const cardW = (pageW - marginX * 2 - 24) / 4;
  const cardH = 62;
  cards.forEach((c, i) => {
    const cx = marginX + i * (cardW + 8);
    doc.setDrawColor(...SLATE_200);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(cx, y, cardW, cardH, 6, 6, "FD");
    doc.setTextColor(...SLATE_500);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(c.l.toUpperCase(), cx + 10, y + 16);
    doc.setFontSize(16);
    doc.setTextColor(...c.tone);
    doc.text(c.v, cx + 10, y + 38);
    if (c.sub) {
      doc.setFontSize(8);
      doc.setTextColor(...SLATE_500);
      doc.setFont("helvetica", "normal");
      doc.text(c.sub, cx + 10, y + 54);
    }
  });
  y += cardH + 22;

  // Programs section (portfolio only)
  if (scope === "portfolio" && programs.length > 0) {
    doc.setTextColor(...SLATE_900);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Programs", marginX, y);
    y += 6;
    const programByProj = (p) => programs.find((pr) => pr.id === p.program_id)?.name || "—";
    const rows = programs.map((pr) => {
      const pprojects = projects.filter((p) => p.program_id === pr.id);
      const est = pprojects.reduce((a, p) => a + p.estimated_cost, 0);
      const actual = pprojects.reduce((a, p) => a + p.actual_cost, 0);
      return [pr.name, pr.owner || "—", pprojects.length, formatCurrency(pr.budget || 0), formatCurrency(est), formatCurrency(actual)];
    });
    autoTable(doc, {
      startY: y + 4,
      head: [["Program", "Owner", "Projects", "Budget", "Est Cost", "Actual Cost"]],
      body: rows,
      styles: { fontSize: 9, cellPadding: 5, textColor: SLATE_900 },
      headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  // Projects detail table
  if (y > doc.internal.pageSize.getHeight() - 180) { doc.addPage({ orientation: "landscape" }); y = 60; }
  doc.setTextColor(...SLATE_900);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Projects", marginX, y);
  const programNameById = new Map(programs.map((p) => [p.id, p.name]));
  const projRows = projects.map((p) => [
    p.name,
    programNameById.get(p.program_id) || "—",
    p.task_count,
    formatCurrency(p.stated_budget),
    formatCurrency(p.estimated_cost),
    formatCurrency(p.actual_cost),
    p.stated_due_date ? formatDate(p.stated_due_date) : "—",
    p.calculated_delivery !== "—" ? formatDate(p.calculated_delivery) : "—",
    p.schedule_variance_days === null || p.schedule_variance_days === undefined ? "—" : p.schedule_variance_days > 0 ? `+${p.schedule_variance_days}d` : `${p.schedule_variance_days}d`,
    `${p.risk_score ?? "—"} ${p.risk_tier ?? ""}`,
  ]);
  autoTable(doc, {
    startY: y + 6,
    head: [["Project", "Program", "Tasks", "Budget", "Est Cost", "Actual Cost", "Stated Due", "Calc Delivery", "Sched Var", "Risk"]],
    body: projRows,
    styles: { fontSize: 8, cellPadding: 4, textColor: SLATE_900 },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" },
      8: { halign: "right" }, 9: { halign: "right" },
    },
    didParseCell: (d) => {
      if (d.section !== "body") return;
      // Risk column tint
      if (d.column.index === 9) {
        const text = String(d.cell.raw || "").toLowerCase();
        if (text.includes("high")) { d.cell.styles.textColor = ROSE; d.cell.styles.fontStyle = "bold"; }
        else if (text.includes("medium")) { d.cell.styles.textColor = AMBER; d.cell.styles.fontStyle = "bold"; }
        else if (text.includes("low")) { d.cell.styles.textColor = EMERALD; }
      }
      // Schedule var column
      if (d.column.index === 8) {
        const raw = String(d.cell.raw || "");
        if (raw.startsWith("+")) { d.cell.styles.textColor = ROSE; d.cell.styles.fontStyle = "bold"; }
        else if (raw !== "—" && !raw.startsWith("0")) { d.cell.styles.textColor = EMERALD; }
      }
    },
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...SLATE_500);
    doc.text(`Compass PM rollup · ${entity.name} · Page ${i} of ${pageCount}`, marginX, doc.internal.pageSize.getHeight() - 20);
  }

  doc.save(`${(entity.name || "rollup").replace(/[^a-z0-9]+/gi, "_")}_${scope}_rollup.pdf`);
}
